import React, { useEffect, useState, useRef } from 'react';
import SideLabel from '@/components/Common/Sides/partials/SideLabel';
import SideLayout from '@/components/Common/Sides/partials/SideLayout';
import SideDescription from '@/components/Common/Sides/partials/SideDescription';
import useCheckout from '@/features/subscription/hooks/useCheckout';
import Seats from '@/features/subscription/components/sides/Seats';
import ProSide from '@/components/Common/Sides/partials/ProSide';
import { usePayment } from '@/features/subscription/hooks/usePayment';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import useAuth from '@/hooks/useAuth';
import {
  fetchPrices,
  setPaymentDetails,
  resetPaymentDetails,
  selectPayment,
} from '@/store/slices/paymentSlice';
import usePaddlePricing from '@/features/subscription/hooks/usePaddlePricing';
import clsx from 'clsx';
import { off, on } from '@/utils/emitter';
import {
  COMMON_EVENTS,
  getUserData,
  logHubxEvent,
} from '@/app/providers/analytics';
import useUpdateEffect from '@/hooks/useUpdateEffect';
import { analytics } from '@/lib/firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { RootState } from '@/store';
import { resolveAnalyticsPaywallType } from '@/features/subscription/utils/analyticsPaywall';
import { formatAnalyticsSource } from '@/helpers/common';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import PaymentInformationModal from '@/features/tool/components/modals/PaymentInformationModal';
import { useInAppPaywallDesign } from '@/features/subscription/remote-config/useInAppPaywallDesign';
import { useFullScreenPaywallEnabled } from '@/features/subscription/remote-config/useFullScreenPaywallEnabled';
import { PAYWALL_VARIANT_BY_DESIGN } from '@/features/tool/constants/paywall';
import { hideSide } from '@/store/slices/sideSlice';
import {
  setFreeTypeForUserAccessLevel,
  setTypeForUserAccessLevel,
} from '@/store/slices/userAccessSlice';
import { checkoutRevealStore } from '@/features/subscription/utils/checkout/checkoutRevealStore';

const LegacyPaymentScreen = ({
  data = {},
}: {
  data?: {
    selectedPriceId?: string;
    variant?: 'default' | 'pro' | 'free';
    interval?: 'month' | 'year' | 'week';
    seats?: number;
    launchCheckout?: boolean;
    toolType?: string;
    originEntryPoint?: string;
  };
}) => {
  const [hasLoggedCheckout, setHasLoggedCheckout] = useState(false);
  const { prices, monthlyPrices, yearlyPrices, weeklyPrices, isLoadingPrices } =
    usePaddlePricing();
  const { isCheckoutOpen, openCheckout, updateCheckout, closeCheckout } =
    useCheckout();
  const { paymentStatus, updatePaymentStatus } = usePayment();

  const { activeSide } = useAppSelector((state) => state.side);
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { selectedPriceId, interval, seats } = useAppSelector(selectPayment);
  const [isBillingDisabled, setIsBillingDisabled] = useState<boolean>(false);
  const typeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.typeForUserAccessLevel
  );
  const freeTypeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.freeTypeForUserAccessLevel
  );
  const paywallType = resolveAnalyticsPaywallType({
    paywallType: typeForUserAccessLevel,
    toolType: data?.toolType,
  });
  const toolSource = data?.toolType
    ? formatAnalyticsSource(data.toolType)
    : undefined;
  const analyticsSource = data?.toolType
    ? buildStructuredAnalyticsSource(data.toolType, data?.originEntryPoint)
    : undefined;
  const checkoutLoadStartRef = useRef<number | null>(null);
  const paywallEventSentRef = useRef(false);
  const checkoutContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    checkoutRevealStore.setRevealTarget(checkoutContainerRef.current);
    return () => checkoutRevealStore.setRevealTarget(null);
  }, []);
  const selectedRawPrice = React.useMemo(
    () =>
      [...weeklyPrices, ...monthlyPrices, ...yearlyPrices].find(
        (price) => price.id === selectedPriceId
      ),
    [monthlyPrices, selectedPriceId, weeklyPrices, yearlyPrices]
  );

  useEffect(() => {
    if (data) {
      dispatch(
        setPaymentDetails({
          selectedPriceId: data.selectedPriceId,
          interval: data?.interval!,
          seats: data?.seats!,
        })
      );
    }
  }, [data]);

  useEffect(() => {
    if (!user) return;
    dispatch(fetchPrices(user?.customerId));
  }, [user, user?.customerId]);

  useEffect(() => {
    if (prices && !selectedPriceId && !data?.selectedPriceId) {
      const defaultPrice =
        prices.single.year ?? prices.single.month ?? prices.single.week;

      if (defaultPrice) {
        dispatch(
          setPaymentDetails({
            selectedPriceId: defaultPrice.id,
            interval: defaultPrice.billing,
            seats: 1,
          })
        );
      }
    }
  }, [prices, selectedPriceId, seats, data?.selectedPriceId]);

  useEffect(() => {
    updatePaymentStatus('idle');
    return () => {
      dispatch(resetPaymentDetails());
      closeCheckout();
    };
  }, []);

  useEffect(() => {
    const handlePlanCancel = () => {
      closeCheckout();
    };

    on('plan:cancel-plan', handlePlanCancel);

    return () => {
      off('plan:cancel-plan');
    };
  }, [selectedPriceId, seats, closeCheckout]);

  useEffect(() => {
    if (paymentStatus === 'failed') {
      dispatch(resetPaymentDetails());
      closeCheckout();
    }
  }, [paymentStatus, closeCheckout]);

  const [isSeatsButtonDisabled, setIsSeatsButtonDisabled] =
    useState<boolean>(false);

  const handleSeatChange = (newSeats: number) => {
    if (!prices) return;
    setIsSeatsButtonDisabled(true);

    const newPrice =
      newSeats >= 2
        ? interval === 'month'
          ? prices.multiple.month
          : prices.multiple.year
        : interval === 'month'
          ? prices.single.month
          : prices.single.year;

    dispatch(
      setPaymentDetails({
        selectedPriceId: newPrice!.id,
        interval: interval,
        seats: newSeats,
      })
    );

    setTimeout(() => {
      setIsSeatsButtonDisabled(false);
    }, 1000);
  };

  useEffect(() => {
    if (data?.launchCheckout) {
      closeCheckout();
      handleSeatChange(data?.seats || 1);
    }
  }, [data?.launchCheckout, data?.seats]);

  useEffect(() => {
    closeCheckout();
  }, [user]);

  // useEffect(() => {
  //   if (planIsActive && plan && !planIsLoading) {
  //     dispatch(hideSide());
  //   }
  // }, [planIsActive, plan, planIsLoading]);

  // useEffect(() => {
  //   if (prices?.single.month) {
  //     if (!user && activeSide === 'paymentScreen') {
  //       dispatch(
  //         showSide({
  //           sideKey: 'register',
  //           sideData: {
  //             openPaywall: true,
  //             selectedPriceId,
  //             interval,
  //             seats,
  //           },
  //         })
  //       );
  //     }
  //   }
  // }, [prices, activeSide, user, selectedPriceId, interval, seats]);

  useEffect(() => {
    if (
      paymentStatus === 'processing' ||
      paymentStatus === 'verifying' ||
      paymentStatus === 'completed'
    ) {
      return;
    }

    if (
      user &&
      prices &&
      selectedPriceId &&
      !isCheckoutOpen &&
      !isLoadingPrices
    ) {
      const checkoutContainer = document.querySelector('.checkout-container');
      if (checkoutContainer) {
        if (!paywallEventSentRef.current) {
          const analyticsUserData = getUserData(user);

          logHubxEvent({
            eventName: COMMON_EVENTS.LND_PAYWALL,
            ...(toolSource ? { source: toolSource } : {}),
            event_params: {
              ...(analyticsSource ? { source: analyticsSource } : {}),
              paywallType,
            } as any,
          });
          logHubxEvent({
            eventName: COMMON_EVENTS.PAYWALL_SEEN,
            event_params: { paywallType },
          });

          if (analytics) {
            logEvent(analytics, 'lnd_paywall', { paywallType });
          }

          const dataLayer = ((window as any).dataLayer ??= []);

          dataLayer.push({
            event: 'lnd_paywall_v2',
            internal_use: false,
            user_data: analyticsUserData,
            paywallType,
          });

          if (freeTypeForUserAccessLevel) {
            setTimeout(() => {
              logHubxEvent({
                eventName: COMMON_EVENTS.LND_PAYWALL,
                ...(toolSource ? { source: toolSource } : {}),
                event_params: {
                  ...(analyticsSource ? { source: analyticsSource } : {}),
                  paywallType: freeTypeForUserAccessLevel,
                } as any,
              });
              logHubxEvent({
                eventName: COMMON_EVENTS.PAYWALL_SEEN,
                event_params: { paywallType: freeTypeForUserAccessLevel },
              });
              analytics &&
                logEvent(analytics, 'lnd_paywall', {
                  paywallType: freeTypeForUserAccessLevel,
                });
              const dataLayer = ((window as any).dataLayer ??= []);

              dataLayer.push({
                event: 'lnd_paywall_v2',
                internal_use: false,
                user_data: analyticsUserData,
                paywallType: freeTypeForUserAccessLevel,
              });
            }, 1000);
          }

          paywallEventSentRef.current = true;
        }

        checkoutLoadStartRef.current = Date.now();
        closeCheckout();
        openCheckout({
          priceId: selectedPriceId,
          quantity: seats || 1,
          toolType: data?.toolType,
          paywallType,
        });
      }
    } else if (
      user &&
      prices &&
      selectedPriceId &&
      isCheckoutOpen &&
      !isLoadingPrices
    ) {
      updateCheckout({
        priceId: selectedPriceId,
        quantity: seats || 1,
        toolType: data?.toolType,
        paywallType,
      });
    }
  }, [
    data?.toolType,
    isCheckoutOpen,
    isLoadingPrices,
    paymentStatus,
    paywallType,
    prices,
    seats,
    selectedPriceId,
    user,
  ]);

  useUpdateEffect(() => {
    if (
      user &&
      prices &&
      selectedPriceId &&
      isCheckoutOpen &&
      !isLoadingPrices &&
      paymentStatus === 'checkout_ready' &&
      activeSide === 'paymentScreen'
    ) {
      const checkoutLoadDuration = checkoutLoadStartRef.current
        ? Date.now() - checkoutLoadStartRef.current
        : undefined;

      if (!hasLoggedCheckout) {
        logHubxEvent({
          eventName: COMMON_EVENTS.CHECKOUT_LOADED,
          event_params: {
            amount: checkoutLoadDuration,
            info: 'success',
            paywallType,
            product_id: selectedRawPrice?.product_id,
            currency: selectedRawPrice?.unit_price?.currency_code,
          },
        });
        if (freeTypeForUserAccessLevel) {
          setTimeout(() => {
            logHubxEvent({
              eventName: COMMON_EVENTS.CHECKOUT_LOADED,
              event_params: {
                amount: checkoutLoadDuration,
                info: 'success',
                paywallType: freeTypeForUserAccessLevel,
                product_id: selectedRawPrice?.product_id,
                currency: selectedRawPrice?.unit_price?.currency_code,
              },
            });
          }, 1000);
        }
        setHasLoggedCheckout(true);
      }
    }
  }, [
    activeSide,
    freeTypeForUserAccessLevel,
    hasLoggedCheckout,
    isCheckoutOpen,
    isLoadingPrices,
    paymentStatus,
    paywallType,
    prices,
    selectedRawPrice,
    user,
  ]);

  // React.useEffect(() => {
  //   console.log('PaymentScreen useEffect triggered', {
  //     prices,
  //     seats,
  //     interval,
  //     paymentStatus,
  //   });
  // }, [prices, seats, interval, paymentStatus]);

  return (
    <div className="relative w-full flex">
      <div className="flex min-h-dvh w-full flex-col items-center justify-start pt-[60px] sm:pt-0">
        <SideLayout className="py-3 !max-w-full !w-full !px-2 md:!px-2">
          <SideLabel
            className="!text-[24px] md:!text-[32px]"
            label={
              data?.variant === 'free'
                ? 'Try PDF Genius Pro for free'
                : 'Get PDF Genius Pro now'
            }
          />
          <SideDescription
            className="!text-[16px] !mt-0"
            text={
              data?.variant === 'free'
                ? 'Unlimited access to all features'
                : 'Unlimited access to all features. Cancel anytime.'
            }
          />
          <div
            className={clsx(
              'flex flex-col md:flex-row justify-start md:justify-center items-center md:items-start md:gap-10',
              isLoadingPrices && 'hidden'
            )}
          >
            {/* {prices && (
              <Seats
                seats={seats}
                setSeats={handleSeatChange}
                billing={interval}
                setBilling={(newBilling: 'month' | 'year') => {
                  setIsBillingDisabled(true);
                  const newPrice =
                    seats >= 2
                      ? newBilling === 'month'
                        ? prices.multiple.month
                        : prices.multiple.year
                      : newBilling === 'month'
                        ? prices.single.month
                        : prices.single.year;
                  dispatch(
                    setPaymentDetails({
                      selectedPriceId: newPrice!.id,
                      interval: newBilling,
                      seats: seats,
                    })
                  );
                  setTimeout(() => {
                    setIsBillingDisabled(false);
                  }, 1000);
                }}
                prices={prices}
                isButtonDisabled={isSeatsButtonDisabled}
                isBillingDisabled={isBillingDisabled}
                isVisible={
                  paymentStatus === 'checkout_ready' || paymentStatus === 'idle'
                }
              />
            )} */}

            <div className="max-w-[400px] !min-w-0 w-full">
              <div ref={checkoutContainerRef} />
            </div>
          </div>
        </SideLayout>
      </div>
      {/* <ProSide variant={data?.variant} billing={interval} /> */}
    </div>
  );
};

const PaymentScreen = ({
  data = {},
}: Parameters<typeof LegacyPaymentScreen>[0]) => {
  const dispatch = useAppDispatch();
  const design = useInAppPaywallDesign();
  const isFullScreen = useFullScreenPaywallEnabled();

  if (design === null) {
    return null;
  }

  if (design === 0) {
    return <LegacyPaymentScreen data={data} />;
  }

  if (isFullScreen === null) {
    return null;
  }

  return (
    <PaymentInformationModal
      data={{
        ...data,
        isOnlyPayment: true,
        paywallVariant: PAYWALL_VARIANT_BY_DESIGN[design] ?? 'paywall1',
        renderInline: true,
        isFullScreen,
        onCancel: () => {
          dispatch(setTypeForUserAccessLevel(null));
          dispatch(setFreeTypeForUserAccessLevel(null));
        },
      }}
      hideModal={() => dispatch(hideSide())}
    />
  );
};

export default PaymentScreen;
